import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { mergeSupplementalObjects } from 'conversations-thread-data/channel-capabilities/public/actions';
import { useConnectedAccounts } from 'conversations-thread-data/connected-accounts/public';
import { trackEvent, getEditorTrackEvent } from '../../../usage-tracking/clients/usageTracker';
import { COMPOSITION_THREAD_ID } from 'conversations-thread-data/communicator/constants/compositionThreadId';
import { showComposeEditor } from '../constants/Events';
// TODO: move to composer-ui
import { createEmailThread } from 'conversations-thread-view/email/actions/createEmailThread';
import { buildCompositionEmptyReply } from 'composer-data/common-reply/public/operators';
import { ThreadDetails } from 'conversations-thread-data/thread-details/public/records';
import AsyncData from 'conversations-async-data/async-data/AsyncData';
// @ts-expect-error module not typed
import { updateSeenNewFeatureShepherds } from 'conversations-thread-view/shepherds/actions/updateSeenNewFeatureShepherds';
import { buildDisabledValidator } from '../../../disable-send/public/operators';
import { getChannelCapabilities, getEditingCapabilities, getFileAttachments } from 'conversations-thread-data/channel-configuration/public/operators';
import { CapabilitiesEditor } from '../../../capability-editor/public/components';
import { EMAIL_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
// @ts-expect-error module not typed
import { getRichText } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
// @ts-expect-error module not typed
import { setRichText } from 'conversations-message-history/common-message-format/operators/commonMessageFormatSetters';
import { uploadBase64Images } from '../../../email/public/clients';
import { SUCCEEDED } from 'conversations-async-data/async-data/constants/asyncStatuses';
// TODO: move to composer-data
import { useChannelInstances } from 'conversations-thread-data/channel-instances/public/hooks';
import { isGenericChannelId } from 'conversations-thread-data/channel-configuration/public/types';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { getUserId } from 'conversations-thread-data/auth/public/selectors';
import { useProsemirrorEditorConfig } from '../../../capability-editor/protected/hooks';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
import { useChannelConfigurations } from 'conversations-thread-data/channel-configuration/public/hooks';
import { getSentMessage } from '../../../usage-tracking/events/messagePublishingInteractions';
import { useDefaultSendFrom } from 'composer-data/senders/public/hooks';
import { jsx as _jsx } from "react/jsx-runtime";
function publishEmail(commonMessage, options) {
  return function (dispatch) {
    const messageRichText = getRichText(commonMessage);
    return uploadBase64Images({
      fileManagerAccess: options.fileManagerAccess,
      threadId: COMPOSITION_THREAD_ID,
      html: messageRichText
    }).then(newMessageRichText => {
      const messageWithUploadedImages = setRichText(newMessageRichText, commonMessage);
      return dispatch(createEmailThread(messageWithUploadedImages, {
        options
      }));
    });
  };
}
const useComponent = ({
  associations = [],
  inboxId,
  onSend,
  onSendStart,
  renderExistingFileSelect,
  replyKey = COMPOSITION_THREAD_ID,
  useWhatsAppCompose,
  defaultMessageValue,
  defaultSendFromEnabled
}) => {
  const dispatch = useDispatch();
  const {
    asyncChannelInstances
  } = useChannelInstances({
    inboxId
  });
  const channelInstances = getData(asyncChannelInstances);
  const {
    asyncConnectedAccounts
  } = useConnectedAccounts({
    inboxId,
    filterNonGlobalAccounts: true
  });
  const connectedAccounts = getData(asyncConnectedAccounts);
  const getInitialState = () => {
    if (!useWhatsAppCompose) return EMAIL_GENERIC_CHANNEL_ID;
    if (defaultMessageValue) return defaultMessageValue.genericChannelId;
    return 'UNSET';
  };
  const [editorConfigKey, setEditorConfigKey] = useState(getInitialState);
  const {
    asyncChannelConfigurations
  } = useChannelConfigurations();
  const configurations = getData(asyncChannelConfigurations);
  const capabilitiesBlob = (configurations === null || configurations === void 0 ? void 0 : configurations.get(editorConfigKey)) || null;
  const {
    defaultSendFrom: defaultTeamSendFrom
  } = useDefaultSendFrom({
    inboxId,
    channelId: isGenericChannelId(editorConfigKey) ? editorConfigKey : null,
    deferred: !defaultSendFromEnabled
  });
  const userId = useSelector(getUserId);
  const emptyReply = useMemo(() => {
    return buildCompositionEmptyReply({
      capabilitiesBlob,
      channelId: editorConfigKey,
      channelInstances,
      connectedAccounts,
      inboxId,
      userId,
      replyKey,
      defaultMessageValue,
      defaultTeamSendFrom
    });
  }, [capabilitiesBlob, channelInstances, connectedAccounts, inboxId, editorConfigKey, userId, replyKey, defaultMessageValue, defaultTeamSendFrom]);
  useEffect(() => {
    trackEvent(showComposeEditor());
  }, []);
  useEffect(() => {
    const supplementalObjects = associations.reduce((acc, association) => {
      if (association.type) acc[association.type] = association.id;
      return acc;
    }, {});
    dispatch(mergeSupplementalObjects({
      capabilityKeys: ImmutableSet([ImmutableMap({
        capability: 'INSERT_CORE',
        threadId: COMPOSITION_THREAD_ID
      })]),
      supplementalObjects
    }));
  }, [dispatch, associations]);
  const fileManagerAccess = editorConfigKey === EMAIL_GENERIC_CHANNEL_ID ? 'HIDDEN_IN_APP_SENSITIVE' : 'HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE';
  const handleSendCommonMessage = useCallback((commonMessage, {
    options
  }) => {
    if (!isGenericChannelId(editorConfigKey)) {
      reportError({
        error: new Error(`Tried to publish new message without a valid genericChannelId`),
        extra: {
          commonMessage,
          genericChannelId: editorConfigKey,
          options
        }
      });
      return;
    }
    onSendStart();
    if (editorConfigKey === EMAIL_GENERIC_CHANNEL_ID) {
      const response = dispatch(publishEmail(commonMessage, Object.assign({}, options, {
        templateId: options !== null && options !== void 0 && options.templateId ? `${options.templateId}` : undefined,
        associations,
        fileManagerAccess
      })));
      onSend(response);
    }
    const ticketsAssociated = associations.filter(({
      type
    }) => type === 'TICKET');
    trackEvent(getSentMessage({
      genericChannelId: editorConfigKey,
      messageChannel: editorConfigKey,
      communicatorType: 'compose',
      timeToReply: -1,
      numberOfTicketsAssociated: ticketsAssociated.length
    }));
  }, [editorConfigKey, dispatch, onSend, onSendStart, associations, fileManagerAccess]);
  const channelCapabilities = capabilitiesBlob && getChannelCapabilities(capabilitiesBlob) || [];
  const editingCapabilities = capabilitiesBlob && getEditingCapabilities(capabilitiesBlob) || [];
  const fileAttachmentsConfig = getFileAttachments(capabilitiesBlob) || {};
  const capabilities = [...channelCapabilities, ...editingCapabilities];
  const capabilitiesConfig = useProsemirrorEditorConfig(capabilities, {
    fileAttachmentsConfig,
    renderExistingFileSelect,
    threadId: COMPOSITION_THREAD_ID
  });
  const sendDisabledValidator = useMemo(() => buildDisabledValidator({
    channelId: editorConfigKey
  }), [editorConfigKey]);
  const threadDetails = new ThreadDetails({
    /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    threadId: COMPOSITION_THREAD_ID,
    inboxId
  });
  const asyncEmptyReply = new AsyncData({
    status: SUCCEEDED,
    data: emptyReply
  });
  const onCloseShepherd = useCallback(({
    allSeenShepherds
  }) => {
    dispatch(updateSeenNewFeatureShepherds({
      value: allSeenShepherds
    }));
  }, [dispatch]);
  return {
    asyncEmptyReply,
    capabilitiesBlob,
    capabilitiesConfig,
    editorConfigKey,
    handleSendCommonMessage,
    onCloseShepherd,
    sendDisabledValidator,
    setActiveChannelId: useWhatsAppCompose ? setEditorConfigKey : () => {},
    threadDetails
  };
};
export const ComposeEditor = ({
  associations,
  inboxId,
  onClose,
  onSend = () => {},
  onSendStart = () => {},
  renderExistingFileSelect,
  replyKey,
  useWhatsAppCompose,
  StyledWrapper,
  defaultMessageValue,
  renderSubmitButton,
  defaultSendFromEnabled = false,
  macrosMenuConfig
}) => {
  const {
    asyncEmptyReply,
    capabilitiesBlob,
    capabilitiesConfig,
    editorConfigKey,
    handleSendCommonMessage,
    onCloseShepherd,
    sendDisabledValidator,
    setActiveChannelId,
    threadDetails
  } = useComponent({
    associations,
    inboxId,
    onSend,
    onSendStart,
    renderExistingFileSelect,
    replyKey,
    useWhatsAppCompose,
    defaultMessageValue,
    defaultSendFromEnabled
  });
  const contextValue = useMemo(() => {
    const trackEventFn = getEditorTrackEvent({
      isComposer: true,
      genericChannelId: editorConfigKey
    });
    return {
      trackEvent: trackEventFn
    };
  }, [editorConfigKey]);
  return /*#__PURE__*/_jsx(EditorTrackerContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(CapabilitiesEditor, {
      asyncEmptyReply: asyncEmptyReply,
      threadDetails: threadDetails,
      isMetadataEditorAbsolute: false,
      isNewEmailComposition: true,
      capabilitiesConfig: capabilitiesConfig,
      sendDisabledValidator: sendDisabledValidator,
      currentChannelId: editorConfigKey,
      capabilitiesBlob: capabilitiesBlob,
      originalGenericChannelId: null,
      latestCMFGenericChannelId: null,
      onCloseShepherd: onCloseShepherd,
      submitHandler: handleSendCommonMessage,
      isComposer: true,
      onClose: onClose,
      onChangeReplyChannel: setActiveChannelId,
      renderExistingFileSelect: renderExistingFileSelect,
      useWhatsAppCompose: useWhatsAppCompose,
      StyledWrapper: StyledWrapper,
      renderSubmitButton: renderSubmitButton,
      defaultSendFromEnabled: defaultSendFromEnabled,
      macrosMenuConfig: macrosMenuConfig
    })
  });
};
ComposeEditor.useComponent = useComponent;
ComposeEditor.displayName = 'ComposeEditor';
ComposeEditor.propTypes = {
  className: PropTypes.string,
  inboxId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  onCloseShepherd: PropTypes.func,
  onSend: PropTypes.func,
  onSendStart: PropTypes.func,
  renderExistingFileSelect: PropTypes.func,
  to: ImmutablePropTypes.list
};
export default ComposeEditor;